import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { ROUTES } from '@shared/config/routes'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { useSubscriptionPricesQuery, applyOverride } from '@features/subscription'
import {
  ProviderPickerModal,
  useBuySubscriptionMutation,
  useDoubleSubmitGuard,
  usePaymentMethodsQuery,
  useStartPayment,
  type ProviderPickerSubmit,
} from '@features/payments'
import './SubscribePage.css'
import '../CardPage/CardPage.css'
import subscribeHero from '../../assets/images/subPage/secret.png'
import image1 from '../../assets/images/subPage/{8153C883-DBCC-46D4-8742-C90292C7AAB7} 1.png'
import image2 from '../../assets/images/subPage/{8153C883-DBCC-46D4-8742-C90292C7AAB7} 2.png'
import image3 from '../../assets/images/subPage/{CB848B3F-7D23-451C-AE61-4A46FA36630D} 1.png'
import image4 from '../../assets/images/subPage/{8153C883-DBCC-46D4-8742-C90292C7AAB7} 3.png'
import image5 from '../../assets/images/subPage/{8153C883-DBCC-46D4-8742-C90292C7AAB7} 4.png'
import image6 from '../../assets/images/subPage/{CB848B3F-7D23-451C-AE61-4A46FA36630D} 2.png'
import image7 from '../../assets/images/subPage/{14EFEA54-4BFC-467A-BC26-D8AF8F92B117} 1.png'
import image8 from '../../assets/images/subPage/{14EFEA54-4BFC-467A-BC26-D8AF8F92B117} 2.png'
import image9 from '../../assets/images/subPage/video1.png'
import image10 from '../../assets/images/subPage/video2.png'
import chanel from '../../assets/images/subPage/chanel.png'
import massage1 from '../../assets/images/subPage/massege1.png'
import massage2 from '../../assets/images/subPage/massege2.png'
import massage3 from '../../assets/images/subPage/massege3.png'
import cloude from '../../assets/images/subPage/cloud.svg'
import { ImageSlider } from '../CardPage/components'

const SubscribePage: React.FC = () => {
  const { data: pricesData } = useSubscriptionPricesQuery()
  // Скрываем устаревшие тарифы (`is_active: false`) — бэк отдаёт их в общем
  // справочнике для отображения старым подписчикам, но новые покупки по ним
  // не разрешены.
  const subscriptionPrices = (pricesData?.subscriptions ?? []).filter(
    p => p.is_active !== false
  )
  const [isPickerOpen, setPickerOpen] = useState(false)
  const [pickerInitialPlanId, setPickerInitialPlanId] = useState<number | null>(
    null
  )
  const [submitError, setSubmitError] = useState<string | null>(null)

  const buyMutation = useBuySubscriptionMutation()
  const guard = useDoubleSubmitGuard()
  const startPayment = useStartPayment()
  const platform = usePlatform()
  const { data: paymentMethodsData } = usePaymentMethodsQuery()
  const paymentMethods = paymentMethodsData?.methods ?? []
  const navigate = useNavigate()

  // Показываем кнопку "Назад" в Telegram WebApp
  useTelegramBackButton(true)

  // Браузерная кнопка «назад»: история, иначе на главную (прямой заход).
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(ROUTES.HOME)
    }
  }

  const scrollToPricing = () => {
    document
      .getElementById('subscription-pricing')
      ?.scrollIntoView({ behavior: 'smooth' })
  }

  const openPicker = (planId: number | null = null) => {
    setPickerInitialPlanId(planId)
    setSubmitError(null)
    setPickerOpen(true)
  }

  const handleSelectPlan = (planId: number) => openPicker(planId)

  /**
   * CTA «Хочу в чат» / «Подробнее»: открыть модалку оплаты.
   * Если тарифы ещё не подгрузились — скроллим к ценам.
   */
  const handlePrimaryCta = () => {
    if (subscriptionPrices.length === 0) {
      scrollToPricing()
      return
    }
    openPicker(null)
  }

  const closeProviderPicker = () => {
    if (buyMutation.isLoading) return
    setPickerOpen(false)
    setSubmitError(null)
  }

  const handleProviderSubmit = ({
    planId,
    provider,
    saveCard,
    paymentMethodId,
  }: ProviderPickerSubmit) => {
    // Замок берём до всего остального: по isLoading второй тап проскакивал и
    // создавал второй счёт.
    if (!guard.tryStart()) return
    setSubmitError(null)
    // Окно открываем синхронно по клику — после await mutate браузер блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    buyMutation.mutate(
      {
        subscription_info_id: planId,
        provider,
        save_card: saveCard,
        payment_method_id: paymentMethodId,
      },
      {
        onSuccess: invoice => {
          startPayment(invoice, tab)
          setPickerOpen(false)
        },
        onError: error => {
          tab.abort()
          const status = (error as { response?: { status?: number } }).response
            ?.status
          if (status === 409) {
            setSubmitError(
              'У вас уже есть активная подписка или незавершённый платёж.'
            )
          } else if (status === 502) {
            setSubmitError(
              'Не удалось связаться с провайдером. Попробуйте ещё раз.'
            )
          } else {
            setSubmitError('Не удалось создать платёж. Попробуйте ещё раз.')
          }
        },
        onSettled: (_invoice, error) => guard.finish(!error),
      }
    )
  }

  return (
    <div className="card-page">
      <button
        type="button"
        className="subscribe-back-button"
        onClick={handleBack}
        aria-label="Назад"
      >
        <ArrowLeft size={22} strokeWidth={2.4} />
      </button>
      <ImageSlider images={[subscribeHero]} title={'Подписка'} />
      <div className="card-page-content">
        <div className="subscribe-hero"></div>
        <div className="card-info">
          <div className="card-info-block">
            <h1 className="card-sub-title">SECRET CHAT</h1>
            <p className="card-sub-subtitle">
              Закрытый Telegram-канал для кондитеров от @pauline_cake
            </p>
          </div>
          <button
            className="payment-option payment-option-rub payment-option-text"
            onClick={handlePrimaryCta}
            disabled={buyMutation.isLoading}
          >
            Подробнее
          </button>
        </div>

        <section className="subscribe-benefits">
          <h2 className="subscribe-benefits-title">Кому полезен чат?</h2>
          <div className="subscribe-benefits-box">
            <ul className="subscribe-benefits-list">
              <li>
                Кондитеру, который в поисках проверенных рецептов и вдохновения;
              </li>
              <li>
                Начинающему кондитеру для получения базовых знаний и поддержки
                опытных коллег;
              </li>
              <li>
                Профессионалу, который ищет способы масштабирования и
                оптимизации процессов.
              </li>
            </ul>
          </div>
        </section>
        <section className="subscribe-benefits">
          <h2 className="subscribe-benefits-title">Что внутри?</h2>
          <div
            className="subscribe-benefits-box"
            style={{ padding: '16px 0px' }}
          >
            <h2 className="subscribe-benefits-subtitle">
              Новый контент еженедельно
            </h2>
            <div className="subscribe-benefits-image-container">
              <img
                src={image1}
                alt="image1"
                className="subscribe-benefits-image"
              />
              <img
                src={image2}
                alt="image2"
                className="subscribe-benefits-image"
              />
              <img
                src={image3}
                alt="image3"
                className="subscribe-benefits-image"
              />
            </div>
            <h2 className="subscribe-benefits-subtitle">
              Пошаговые видеоуроки и подробные PDF-рецепты
            </h2>
            <div className="subscribe-benefits-image-container">
              <div className="subscribe-benefits-image-container-item">
                <img
                  src={image6}
                  alt="image1"
                  className="subscribe-benefits-image"
                />
                <p>1</p>
              </div>
              <div className="subscribe-benefits-image-container-item">
                <img
                  src={image4}
                  alt="image2"
                  className="subscribe-benefits-image"
                />
                <p>2</p>
              </div>
              <div className="subscribe-benefits-image-container-item">
                <img
                  src={image5}
                  alt="image3"
                  className="subscribe-benefits-image"
                />
                <p>3</p>
              </div>
            </div>
            <h2 className="subscribe-benefits-subtitle">
              Экспертные лекции и разборы{' '}
            </h2>
            <div className="subscribe-benefits-image-container-horizontal">
              <img src={image9} alt="image9" />
              <img src={image10} alt="image10" />
            </div>
            <h2 className="subscribe-benefits-subtitle">Помощь куратора</h2>
            <div className="subscribe-benefits-image-container-chat">
              <img
                src={image7}
                alt="image1"
                className="subscribe-benefits-image-chat"
              />
              <img
                src={image8}
                alt="image2"
                className="subscribe-benefits-image-chat"
              />
            </div>
            <h2 className="subscribe-benefits-subtitle">
              Комьюнити кондитеров и любителей выпечки
            </h2>
            <div className="subscribe-benefits-image-container-community">
              <img src={chanel} alt="chanel1" />
            </div>
            <h2 className="subscribe-benefits-subtitle">
              Общение с Полиной по теме и не только:)
            </h2>
            <div className="subscribe-benefits-image-container-community-pauline">
              <img src={massage1} alt="massage1" />
              <img src={massage2} alt="massage2" />
              <img src={massage3} alt="massage3" />
            </div>
          </div>
        </section>
        <div className="subscription-pricing-block" id="subscription-pricing">
          <img src={cloude} className="cloude-image cloude-image-1" alt="" />
          <img src={cloude} className="cloude-image cloude-image-2" alt="" />
          <img src={cloude} className="cloude-image cloude-image-3" alt="" />
          <h3 className="pricing-title">Стоимость подписки</h3>

          <div className="pricing-options">
            {subscriptionPrices.map(price => {
              const eff = applyOverride(price, pricesData?.override)
              return (
                <button
                  key={price.subscription_info_id}
                  type="button"
                  className="pricing-option"
                  onClick={() => handleSelectPlan(price.subscription_info_id)}
                  disabled={buyMutation.isLoading}
                >
                  <div className="price-main">
                    {eff.hasDiscount && (
                      <p className="price-main-old">
                        {eff.basePrice.toLocaleString()}Р
                      </p>
                    )}
                    <p className="price-main-text">
                      {eff.finalPrice.toLocaleString()}Р
                    </p>
                    <p className="price-period">{price.suffix}</p>
                  </div>
                  {price.description && (
                    <div className="price-discount">{price.description}</div>
                  )}
                </button>
              )
            })}
          </div>

          <button
            className="subscribe-button page-button"
            onClick={handlePrimaryCta}
            disabled={buyMutation.isLoading}
          >
            Хочу в чат
          </button>
        </div>
      </div>

      {isPickerOpen && (
        <ProviderPickerModal
          plans={subscriptionPrices}
          override={pricesData?.override}
          paymentMethods={paymentMethods}
          initialPlanId={pickerInitialPlanId}
          onSubmit={handleProviderSubmit}
          onClose={closeProviderPicker}
          isSubmitting={buyMutation.isLoading}
          errorText={submitError}
        />
      )}
    </div>
  )
}

export default SubscribePage
